import type { FormFieldRef, TFormFieldDict } from 'components/form/types/field'
import type { TFormModel } from 'components/form/types/formModel'
import { reactive, type ComputedRef, type Ref, unref } from 'vue'
import { evaluateFormState, patchAllFields } from './utils'
import {
  validateFormOrField,
  type FormError,
  type FormErrorMap
} from 'components/form/validation'
import type { StringIndexable } from '@/types/object'
import type { UseFormStatsReturn } from './useFormStats'
import type { UseFormProps } from '.'
import { mergeDicts } from '@/utils/object'
import type { IForm } from 'components/form/types/form'

export type UseFormErrorsOptions = {
  showErrorsOnBlur?: boolean;
}

export type UseFormErrorsProps<T extends TFormModel = { [key: string]: any }, ContextType extends (StringIndexable | undefined) = StringIndexable> = UseFormProps<T, ContextType> & {
  fields: Ref<TFormFieldDict<T>> | ComputedRef<TFormFieldDict<T>>;
  stats: UseFormStatsReturn<T>;
  context?: Ref<ContextType>;
  options?: UseFormErrorsOptions;
}

export type UseFormErrorsState = {
  errors: FormErrorMap;
  errorCount: number;
  visible: boolean;
}

export const useFormErrors = <T extends StringIndexable = StringIndexable, ContextType extends (StringIndexable | undefined) = StringIndexable>(
  props: UseFormErrorsProps<T, ContextType>
) => {
  const state = reactive<UseFormErrorsState>({
    errors: {} as FormErrorMap,
    errorCount: 0,
    visible: false
  })


  const updateErrors = (next: FormErrorMap, count: number) => {
    state.errors = mergeDicts(state.errors, next);
    state.errorCount = count;
  }

  const updateFieldErrors = (field: FormFieldRef, fieldSchema?: IForm<any>) => {
    const [errmap, count] = validateFormOrField(field, fieldSchema || field.value.schema, evaluateFormState(unref(props.schema) as IForm<T, ContextType>, props.fields.value), (errorField, errors) => {
      errorField.value.errors = errors;
    });

    updateErrors(errmap, count);
    //state.errors = mergeDicts(state.errors, errmap);
    //state.errorCount = Math.max(state.errorCount, count);
  }

  const refreshErrors = (evaluated?: any, updateFields: boolean = true, forceVisible: boolean = false) => {
    evaluated = evaluated || evaluateFormState(unref(props.schema) as IForm<T, ContextType>, props.fields.value)
    const [errmap, count] = validateFormOrField<T, ContextType>(
      props.fields.value,
      unref(props.schema) as IForm<T, ContextType>,
      evaluated,
      (errorField: FormFieldRef, errors: FormError[]) => {
        if (updateFields) {
          errorField.value.errors = props.options?.showErrorsOnBlur ? errors.map(err => ({ ...err, hidden: forceVisible ? false : true })) : errors
        }
      }
    )
    updateErrors(errmap, count);
    //state.errors = errmap
    //state.errorCount = count
  }

  const setErrorsVisible = (visible: boolean) => {
    state.visible = visible;
  }

  const clearErrors = () => {
    state.errors = {};
    state.errorCount = 0;
    state.visible = false;

    patchAllFields(props.fields.value, (field) => {
      field.value.errors = [];
    });
  }

  const setFieldErrorsVisible = (field: FormFieldRef, visible: boolean) => {
    //if (!visible) {
    //  const errors = (field.value.errors || []);
    //  state.errorCount = Math.max(0, state.errorCount-errors.length);
    //} else {
    //  const errors = (field.value.errors || []);
    //  state.errorCount = Math.max(state.errorCount, errors.length);
    //}

    const errors = (field.value.errors || []).map(err => ({...err, hidden: !visible}));
    field.value.errors = errors;
  }

  const refresh = (args: { forceVisible?: boolean } = {}): Promise<number> => {
    return new Promise<number>((resolve) => {
      refreshErrors(undefined, true, !!args.forceVisible);

      queueMicrotask(() => {
        resolve(state.errorCount);
      })
    })

  }

  return { state, refreshErrors, refresh, setErrorsVisible, clearErrors, updateFieldErrors, setFieldErrorsVisible }
}

export type UseFormErrorsReturn<T extends StringIndexable = StringIndexable, ContextType extends (StringIndexable | undefined) = StringIndexable> = ReturnType<
  typeof useFormErrors<T, ContextType>
>
